import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { QrCode } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/common/Card';
import ShareButtons from '../../components/obituary/ShareButtons';
import { useToast } from '../../context/ToastContext';
import { fetchObituaries } from '../../utils/api';

/**
 * 동적 OG/메타 태그 설정 (react-helmet 미사용 → 직접 DOM 조작)
 * 언마운트 시 원복하여 다른 페이지에 영향 없도록 처리
 */
const useObituaryMeta = (obituary) => {
  useEffect(() => {
    if (!obituary) return undefined;

    const prevTitle = document.title;
    const title = `故 ${obituary.deceasedName} 부고`;
    const description = `${obituary.funeralHome} ${obituary.funeralRoom} · 발인 ${obituary.departureDate} ${obituary.departureTime}`;

    document.title = title;

    // 동적으로 추가한 메타 태그 추적 (언마운트 시 제거)
    const created = [];
    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('name', 'description', description);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [obituary]);
};

/**
 * ObituarySharePage - 부고 공유 페이지
 * 부고 공유 옵션 및 미리보기 제공
 */
const ObituarySharePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();

  const [obituary, setObituary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 부고 데이터 불러오기
  useEffect(() => {
    const loadObituary = async () => {
      try {
        setIsLoading(true);
        const obituaries = await fetchObituaries();
        const found = obituaries.find(o => o.id === parseInt(id));

        if (!found) {
          showToast('부고를 찾을 수 없습니다.', 'error');
          navigate('/obituaries');
          return;
        }

        setObituary(found);
      } catch (error) {
        console.error('Failed to load obituary:', error);
        showToast('부고를 불러오는데 실패했습니다.', 'error');
        navigate('/obituaries');
      } finally {
        setIsLoading(false);
      }
    };

    loadObituary();
  }, [id, navigate, showToast]);

  // 동적 OG/메타 태그 (SNS 공유 미리보기)
  useObituaryMeta(obituary);

  // 로딩 상태
  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-gray-600">부고를 불러오는 중...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 데이터 없음
  if (!obituary) {
    return null;
  }

  // 날짜 포맷팅
  const formattedDepartureDate = format(
    new Date(obituary.departureDate),
    'yyyy년 MM월 dd일 (E)',
    { locale: ko }
  );

  // 공유용 상세 링크 + QR 코드 (빈소 부착용)
  const detailUrl = `${window.location.origin}/obituaries/${obituary.id}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(detailUrl)}`;

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            부고 공유하기
          </h1>
          <p className="text-gray-600">
            소중한 분의 부고를 주변에 전달하세요.
          </p>
        </div>

        {/* Preview */}
        <Card className="mb-8">
          <h2 className="text-lg font-semibold text-primary-900 mb-4 pb-3 border-b">
            미리보기
          </h2>

          <div className="space-y-4">
            {/* 고인 정보 */}
            <div className="text-center border-b pb-4">
              <h3 className="text-xl font-bold text-primary-900 mb-2">
                故 {obituary.deceasedName}
              </h3>
              <p className="text-sm text-gray-600">
                {obituary.chiefMourner} {obituary.chiefMournerRelationship}의 {obituary.relationship}
              </p>
            </div>

            {/* 장례식장 정보 */}
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">빈소</span>
                <span className="text-gray-900">{obituary.funeralHome} {obituary.funeralRoom}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">주소</span>
                <span className="text-gray-900">{obituary.funeralAddress}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">발인</span>
                <span className="text-gray-900">{formattedDepartureDate} {obituary.departureTime}</span>
              </div>
            </div>

            {/* 상주 정보 */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">상주</span>
                <span className="text-gray-900">{obituary.chiefMourner} ({obituary.chiefMournerRelationship})</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">연락처</span>
                <span className="text-gray-900">{obituary.chiefMournerPhone}</span>
              </div>

              {/* 추가 상주 */}
              {obituary.additionalMourners && obituary.additionalMourners.length > 0 && (
                <div className="flex">
                  <span className="w-24 font-semibold text-gray-700">추가상주</span>
                  <div className="space-y-1">
                    {obituary.additionalMourners.map((mourner, index) => (
                      <div key={index} className="text-gray-900">
                        {mourner.name} ({mourner.relationship})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Share Options */}
        <Card>
          <h2 className="text-lg font-semibold text-primary-900 mb-4 pb-3 border-b">
            공유 방법 선택
          </h2>

          <ShareButtons obituary={obituary} />
        </Card>

        {/* QR 코드 (빈소 부착용) */}
        <Card className="mt-8">
          <h2 className="text-lg font-semibold text-primary-900 mb-4 pb-3 border-b flex items-center gap-2">
            <QrCode size={20} /> QR 코드
          </h2>
          <div className="flex flex-col items-center text-center">
            <div className="rounded-lg border border-accent-200 bg-white p-3">
              <img
                src={qrSrc}
                alt={`故 ${obituary.deceasedName} 부고 QR 코드`}
                width={180}
                height={180}
                className="h-44 w-44 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div
                className="hidden h-44 w-44 items-center justify-center rounded bg-accent-50 text-sm text-gray-500"
                style={{ display: 'none' }}
              >
                QR 코드를 불러올 수 없습니다
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              QR 코드를 인쇄하여 빈소에 부착하면 부고 페이지로 바로 연결됩니다.
            </p>
            <a
              href={qrSrc}
              download={`obituary-${obituary.id}-qr.png`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-accent-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-accent-50"
            >
              QR 이미지 저장
            </a>
          </div>
        </Card>

        {/* Info */}
        <div className="mt-8 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold">안내:</span> 공유 링크를 통해 부고 정보를 확인할 수 있습니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituarySharePage;
